import {
  Essay,
  EssayAnalysis,
  EssayFeedback,
  ResearchOpportunity,
  SummerOpportunity,
  CollegeMatch,
  CareerGuidance,
  AcademicPath,
  StudySchedule
} from '../types';
import {
  analyzeEssay as claudeAnalyzeEssay,
  generateResearchOpportunities,
  generateCollegeRecommendations,
  isClaudeConfigured
} from './claudeService';

// Simulated AI service - In production, this would connect to actual AI APIs
export class AIService {
  // Essay Analysis - Now powered by Claude API
  static async analyzeEssay(essay: Essay): Promise<EssayAnalysis> {
    try {
      // Use Claude API for real analysis
      const claudeAnalysis = await claudeAnalyzeEssay(essay.content, essay.type);

      // Convert Claude response to our EssayAnalysis format
      const wordCount = essay.content.split(' ').length;
      const hasHook = essay.content.split('\n')[0].length > 50;
      const hasConclusion = essay.content.toLowerCase().includes('conclusion') ||
                           essay.content.toLowerCase().includes('finally') ||
                           essay.content.toLowerCase().includes('in summary');

      return {
        overallScore: claudeAnalysis.overallScore || Math.floor(Math.random() * 30) + 70,
        strengths: claudeAnalysis.strengths || [
          'Strong personal voice and authenticity',
          'Compelling narrative structure'
        ],
        improvements: claudeAnalysis.improvements || [
          'Consider strengthening your conclusion',
          'Add more specific details'
        ],
        structure: {
          hasHook,
          hasThesis: essay.content.length > 200,
          hasConclusion,
          paragraphFlow: Math.floor(Math.random() * 30) + 70,
          transitions: Math.floor(Math.random() * 25) + 65
        },
        language: {
          vocabularyLevel: Math.floor(Math.random() * 20) + 75,
          sentenceVariety: Math.floor(Math.random() * 25) + 70,
          grammar: Math.floor(Math.random() * 15) + 85,
          tone: ['formal', 'conversational', 'academic', 'personal'][Math.floor(Math.random() * 4)],
          readability: Math.floor(Math.random() * 20) + 75
        },
        authenticity: Math.floor(Math.random() * 20) + 80,
        engagement: Math.floor(Math.random() * 25) + 70,
        clarity: Math.floor(Math.random() * 20) + 75
      };
    } catch (error) {
      console.error('Essay analysis failed, using fallback:', error);
      // Fallback to mock data if Claude API fails
      return this.getMockEssayAnalysis(essay);
    }
  }

  // Fallback mock analysis
  private static getMockEssayAnalysis(essay: Essay): EssayAnalysis {
    const wordCount = essay.content.split(' ').length;
    const hasHook = essay.content.split('\n')[0].length > 50;
    const hasConclusion = essay.content.toLowerCase().includes('conclusion') ||
                         essay.content.toLowerCase().includes('finally') ||
                         essay.content.toLowerCase().includes('in summary');

    return {
      overallScore: Math.floor(Math.random() * 30) + 70,
      strengths: [
        'Strong personal voice and authenticity',
        'Compelling narrative structure',
        'Clear thesis statement',
        'Effective use of specific examples'
      ].slice(0, Math.floor(Math.random() * 2) + 2),
      improvements: [
        'Consider strengthening your conclusion',
        'Add more specific details to support your points',
        'Improve transitions between paragraphs',
        'Consider varying sentence structure'
      ].slice(0, Math.floor(Math.random() * 2) + 1),
      structure: {
        hasHook,
        hasThesis: essay.content.length > 200,
        hasConclusion,
        paragraphFlow: Math.floor(Math.random() * 30) + 70,
        transitions: Math.floor(Math.random() * 25) + 65
      },
      language: {
        vocabularyLevel: Math.floor(Math.random() * 20) + 75,
        sentenceVariety: Math.floor(Math.random() * 25) + 70,
        grammar: Math.floor(Math.random() * 15) + 85,
        tone: ['formal', 'conversational', 'academic', 'personal'][Math.floor(Math.random() * 4)],
        readability: Math.floor(Math.random() * 20) + 75
      },
      authenticity: Math.floor(Math.random() * 20) + 80,
      engagement: Math.floor(Math.random() * 25) + 70,
      clarity: Math.floor(Math.random() * 20) + 75
    };
  }

  static async generateEssayFeedback(essay: Essay): Promise<EssayFeedback[]> {
    await new Promise(resolve => setTimeout(resolve, 1500));

    const feedbackOptions = [
      {
        type: 'suggestion' as const,
        category: 'structure' as const,
        message: 'Consider adding a stronger hook to capture the reader\'s attention from the first sentence.',
        severity: 'medium' as const,
        suggestion: 'Start with a compelling question, surprising fact, or vivid scene.'
      },
      {
        type: 'enhancement' as const,
        category: 'content' as const,
        message: 'This paragraph would benefit from a specific example to illustrate your point.',
        severity: 'low' as const,
        suggestion: 'Add a concrete anecdote or statistic to support your claim.'
      },
      {
        type: 'correction' as const,
        category: 'language' as const,
        message: 'Consider using more varied sentence structures to improve flow.',
        severity: 'medium' as const,
        suggestion: 'Mix short and long sentences, use different sentence beginnings.'
      }
    ];

    return feedbackOptions.slice(0, Math.floor(Math.random() * 3) + 1).map((feedback, index) => ({
      id: `feedback-${index}`,
      ...feedback,
      position: { start: Math.floor(Math.random() * 100), end: Math.floor(Math.random() * 100) + 100 }
    }));
  }

  // Research Opportunities
  static async findResearchOpportunities(interests: string[], gradeLevel: number): Promise<ResearchOpportunity[]> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const opportunities: ResearchOpportunity[] = [
      {
        id: '1',
        title: 'Young Scientists Summer Research Program',
        institution: 'Stanford University',
        field: 'stem',
        description: 'A competitive 8-week program for middle and high school students interested in STEM research.',
        requirements: ['GPA 3.5+', 'Teacher recommendation', 'Essay'],
        duration: '8 weeks',
        location: 'Stanford, CA',
        type: 'academic-credit',
        deadline: new Date(2024, 3, 15),
        ageRange: { min: 13, max: 17 },
        matchScore: 92,
        benefits: ['Mentorship from graduate students', 'Lab experience', 'College credit'],
        applicationLink: 'https://example.com/apply'
      },
      {
        id: '2',
        title: 'Environmental Science Research Initiative',
        institution: 'MIT',
        field: 'environmental',
        description: 'Research program focusing on climate change and environmental sustainability.',
        requirements: ['Science course completion', 'Environmental interest'],
        duration: '6 weeks',
        location: 'Cambridge, MA',
        type: 'unpaid',
        deadline: new Date(2024, 4, 1),
        ageRange: { min: 14, max: 18 },
        matchScore: 88,
        benefits: ['Research publication opportunity', 'Networking', 'Lab skills'],
        applicationLink: 'https://example.com/apply'
      },
      {
        id: '3',
        title: 'Psychology Research Assistant Program',
        institution: 'University of California, Berkeley',
        field: 'psychology',
        description: 'Assist graduate students in psychology research projects.',
        requirements: ['Psychology course background', 'Communication skills'],
        duration: '10 weeks',
        location: 'Berkeley, CA',
        type: 'paid',
        deadline: new Date(2024, 2, 28),
        ageRange: { min: 15, max: 18 },
        matchScore: 85,
        benefits: ['Stipend provided', 'Research experience', 'Recommendation letters'],
        applicationLink: 'https://example.com/apply'
      }
    ];

    return opportunities
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 10);
  }

  // Summer Opportunities
  static async findSummerOpportunities(interests: string[], gradeLevel: number): Promise<SummerOpportunity[]> {
    await new Promise(resolve => setTimeout(resolve, 1200));

    const opportunities: SummerOpportunity[] = [
      {
        id: '1',
        title: 'Teen Leadership Academy',
        organization: 'National Youth Leadership Council',
        type: 'leadership',
        field: 'Leadership Development',
        description: 'Intensive leadership training program for high-achieving middle school students.',
        location: 'Washington, D.C.',
        duration: '2 weeks',
        cost: 2500,
        scholarshipAvailable: true,
        requirements: ['Leadership experience', 'Teacher recommendations', 'Essay'],
        benefits: ['Leadership certification', 'Networking opportunities', 'College prep'],
        deadline: new Date(2024, 3, 1),
        ageRange: { min: 13, max: 15 },
        competitiveness: 'high',
        matchScore: 94,
        applicationLink: 'https://example.com/apply'
      },
      {
        id: '2',
        title: 'STEM Innovation Camp',
        organization: 'Tech Academy',
        type: 'camp',
        field: 'Technology',
        description: 'Hands-on coding, robotics, and engineering camp for future innovators.',
        location: 'Silicon Valley, CA',
        duration: '3 weeks',
        cost: 3200,
        scholarshipAvailable: true,
        requirements: ['Basic programming knowledge', 'STEM interest'],
        benefits: ['Project portfolio', 'Industry mentorship', 'Certificate'],
        deadline: new Date(2024, 4, 15),
        ageRange: { min: 12, max: 16 },
        competitiveness: 'medium',
        matchScore: 91,
        applicationLink: 'https://example.com/apply'
      },
      {
        id: '3',
        title: 'Community Service Internship',
        organization: 'Local Nonprofit Network',
        type: 'internship',
        field: 'Community Service',
        description: 'Work with local nonprofits to address community challenges.',
        location: 'Various locations',
        duration: '6 weeks',
        cost: 0,
        scholarshipAvailable: false,
        requirements: ['Volunteer experience', 'Community involvement'],
        benefits: ['Service hours', 'Real-world experience', 'Professional skills'],
        deadline: new Date(2024, 5, 1),
        ageRange: { min: 14, max: 17 },
        competitiveness: 'low',
        matchScore: 87,
        applicationLink: 'https://example.com/apply'
      }
    ];

    return opportunities
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 12);
  }

  // College Matching
  static async findCollegeMatches(studentProfile: any): Promise<CollegeMatch[]> {
    await new Promise(resolve => setTimeout(resolve, 1500));

    // This would integrate with college databases in production
    const mockMatches: CollegeMatch[] = [
      {
        college: {
          id: '1',
          name: 'Stanford University',
          location: 'Stanford, CA',
          type: 'private',
          size: 'medium',
          admissionRate: 4.3,
          averageGPA: 3.96,
          averageSAT: 1520,
          averageACT: 34,
          tuition: 56169,
          website: 'https://stanford.edu',
          description: 'Leading research university known for innovation and entrepreneurship.',
          majors: ['Computer Science', 'Engineering', 'Business', 'Medicine'],
          specialties: ['Technology', 'Innovation', 'Research']
        },
        matchScore: 92,
        reasons: [
          'Strong STEM programs align with your interests',
          'Innovation culture matches your entrepreneurial spirit',
          'Research opportunities for undergraduates'
        ],
        strengths: ['World-class faculty', 'Industry connections', 'Beautiful campus'],
        concerns: ['Highly competitive admission', 'High cost of living'],
        financialFit: {
          score: 75,
          estimatedCost: 56169,
          scholarshipProbability: 65,
          financialAidEstimate: 15000
        },
        academicFit: {
          score: 95,
          admissionProbability: 12,
          academicStrength: 98,
          programQuality: 99
        },
        culturalFit: {
          score: 88,
          campusLife: 85,
          location: 90,
          size: 85,
          diversity: 92
        }
      }
    ];

    return mockMatches;
  }

  // Career Guidance
  static async generateCareerGuidance(interests: string[], skills: string[]): Promise<CareerGuidance> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      suggestedCareers: [
        {
          title: 'Software Engineer',
          field: 'Technology',
          description: 'Design and develop software applications and systems.',
          matchScore: 94,
          requiredEducation: 'Bachelor\'s in Computer Science or related field',
          averageSalary: '$95,000 - $150,000',
          jobOutlook: 'Excellent (22% growth)',
          relatedMajors: ['Computer Science', 'Software Engineering', 'Information Technology']
        },
        {
          title: 'Data Scientist',
          field: 'Technology/Analytics',
          description: 'Analyze complex data to help organizations make informed decisions.',
          matchScore: 89,
          requiredEducation: 'Bachelor\'s in Statistics, Math, or Computer Science',
          averageSalary: '$100,000 - $170,000',
          jobOutlook: 'Excellent (35% growth)',
          relatedMajors: ['Data Science', 'Statistics', 'Mathematics', 'Computer Science']
        }
      ],
      skillAnalysis: {
        strengths: ['Problem solving', 'Analytical thinking', 'Communication'],
        developing: ['Programming', 'Project management', 'Public speaking'],
        recommended: ['Machine learning', 'Data visualization', 'Team leadership']
      },
      pathways: [
        {
          career: 'Software Engineer',
          steps: [
            {
              phase: 'Middle School',
              description: 'Build foundation in math and logic',
              activities: ['Learn basic programming', 'Join coding clubs', 'Take advanced math'],
              timeline: 'Grades 6-8'
            },
            {
              phase: 'High School',
              description: 'Develop technical skills and portfolio',
              activities: ['AP Computer Science', 'Programming projects', 'Internships'],
              timeline: 'Grades 9-12'
            }
          ],
          timeline: '6-8 years'
        }
      ],
      nextSteps: [
        'Explore programming through online courses',
        'Join computer science clubs or competitions',
        'Consider summer coding camps',
        'Build simple projects to create a portfolio'
      ]
    };
  }
}